import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { endpoints, type RecipeSummary } from '../api/endpoints';
import { ApiException } from '../api/client';
import { RecipeCard } from '../components/RecipeCard';
import { Skeleton, ErrorState, EmptyState } from '../components/states';
import { cuisineLabel } from '../lib/cuisines';

// «Все блюда кухни» — полноэкранная лента RecipeCard (как SlotPickerModal), фильтр cuisine из этапа A.
export function CuisineScreen() {
  const { key = '' } = useParams();
  const nav = useNavigate();
  const [items, setItems] = useState<RecipeSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setError(null);
    setItems(null);
    void endpoints.getRecipesByCuisine(key, 100).then(
      (r) => setItems(r.items),
      (e) => setError(e instanceof ApiException ? e.body.message : 'Не удалось загрузить'),
    );
  }, [key]);

  useEffect(() => {
    // Разовая загрузка при смене кухни — намеренный fetch-in-effect (план без TanStack).
    // eslint-disable-next-line react-hooks/set-state-in-effect
    load();
  }, [load]);

  return (
    <div className="px-4 pt-6 pb-[calc(2rem+env(safe-area-inset-bottom))]">
      <div className="mb-4 flex items-center gap-3">
        <button onClick={() => nav(-1)} aria-label="Назад" className="text-xl text-ink/60">
          ←
        </button>
        <h1 className="text-2xl font-bold text-ink">{cuisineLabel(key)}</h1>
      </div>
      {error ? (
        <ErrorState message={error} onRetry={load} />
      ) : items === null ? (
        <div className="space-y-4">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-44 w-full" />
          ))}
        </div>
      ) : items.length === 0 ? (
        <EmptyState title="Пока нет блюд" hint="Скоро добавим рецепты этой кухни." />
      ) : (
        <div className="stagger space-y-4">
          {items.map((r) => (
            <RecipeCard
              key={r.id}
              title={r.title ?? ''}
              imageUrl={r.imageUrl}
              cookTimeMin={r.cookTimeMin}
              servings={r.servings}
              onClick={() => nav(`/recipe/${r.id}`)}
            />
          ))}
        </div>
      )}
    </div>
  );
}
